import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { Modal } from '../../components/Modal';
import { useAuth } from '../auth/AuthContext';

/**
 * AskMAK 24/7 doubt-solve chat — doubtModal + openDoubt()/askDoubt()
 * (index.html:4300). A chat box that posts each question to
 * POST /courses/:slug/doubt, which returns an instant answer matched from the
 * course question bank or a "logged for a mentor" reply (see community.askDoubt).
 *
 * Opened from the home "AskMAK 24/7" pillar and the player tools row. Login is
 * required (the endpoint is authed) — the UI action gates that before opening.
 */
interface Msg {
  who: 'bot' | 'you';
  text: string;
}

export function DoubtModal({ courseSlug, onClose }: { courseSlug: string; onClose: () => void }) {
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] ?? 'there';
  const [log, setLog] = useState<Msg[]>([
    { who: 'bot', text: `Hi ${firstName}! Ask me anything about your course. If I can't help, I'll pass it to a mentor.` },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log]);

  async function ask(): Promise<void> {
    const q = input.trim();
    if (!q || busy) return;
    setInput('');
    setLog((l) => [...l, { who: 'you', text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post<{ answered: boolean; reply: string }>(`/courses/${courseSlug}/doubt`, { question: q });
      setLog((l) => [...l, { who: 'bot', text: data.reply }]);
    } catch {
      setLog((l) => [...l, { who: 'bot', text: 'Sorry — something went wrong sending that. Please try again.' }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} label="Doubt solve">
      <h2 style={{ fontSize: 18 }}>💡 Doubt solve — 24/7</h2>
      <p className="msub">Instant answers from the assistant. Anything it can&apos;t solve goes to a mentor.</p>
      <div className="db-log" data-testid="doubt-log" ref={logRef}>
        {log.map((m, i) => (
          <div className={`db-msg ${m.who}`} key={i}>
            <b>{m.who === 'bot' ? 'MAKTech Assistant' : 'You'}</b>
            <p>{m.text}</p>
          </div>
        ))}
      </div>
      <div className="aform">
        <input
          data-testid="doubt-input"
          placeholder="Type your question…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void ask();
          }}
        />
        <button className="btn btn-primary btn-sm" onClick={() => void ask()} disabled={busy} data-testid="doubt-ask">
          {busy ? '…' : 'Ask'}
        </button>
      </div>
      <p className="asub" style={{ marginTop: 10, fontSize: 12 }}>
        Weekly live doubt-solving class: every Friday 9:00 PM (BD time) — check Live classes.
      </p>
    </Modal>
  );
}
